// Client wrapper for the Socket.IO realtime multiplayer relay.
// The server matches two signed-in players and relays every action back to both
// of them (the sender included, as an echo) so both clients apply the exact
// same action sequence. The server also generates the shared shuffled deck, so
// the two clients simulate the same game deterministically.
import { io, Socket } from 'socket.io-client';
import type { Card } from './component/Cards';

// Every action the game engine can emit. The acting player's client sends it;
// the server relays it to BOTH clients, which then apply it.
export type OnlineAction =
  | { type: 'move'; from: [number, number]; to: [number, number] }
  | { type: 'card-activate'; instanceId: string }
  | { type: 'card-lock'; instanceId: string }
  | { type: 'throw'; instanceId: string; targetId: string }
  | { type: 'draw-card'; instanceId: string }
  | { type: 'lock'; instanceId: string; row: number; col: number }
  | { type: 'discard'; instanceId: string }
  | { type: 'timeout'; player: number };

export type MatchStart = {
  room: string;
  side: 1 | 2;
  opponent: string;
  deck: Card[];
  hands: { 1: Card[]; 2: Card[] };
  firstPlayer: number;
};

type Handler = (payload: unknown) => void;

class MultiplayerConnection {
  private socket: Socket | null = null;
  private handlers: Record<string, Handler[]> = {};
  private connectPromise: Promise<void> | null = null;

  /** Connect to the relay (idempotent) and resolve once the socket is up. */
  connect(): Promise<void> {
    if (this.socket?.connected) {
      return Promise.resolve();
    }
    if (this.connectPromise) {
      return this.connectPromise;
    }
    this.connectPromise = new Promise((resolve, reject) => {
      // `io()` connects to the same origin the page is served from. In dev that
      // is the CRA dev server, which proxies /socket.io (polling + websocket)
      // to the backend. The httpOnly session cookie travels with the handshake.
      const socket = io({
        withCredentials: true,
        transports: ['websocket', 'polling'],
      });
      this.socket = socket;

      socket.on('connect', () => {
        this.connectPromise = null;
        resolve();
      });
      socket.on('connect_error', (err) => {
        this.connectPromise = null;
        this.socket?.disconnect();
        this.socket = null;
        reject(err);
      });

      // forward server events to registered React listeners
      socket.on('match-queued', (data) => this.dispatch('match-queued', data));
      socket.on('match-start', (data) => this.dispatch('match-start', data));
      socket.on('action', (data) => this.dispatch('action', data));
      socket.on('opponent-left', () => this.dispatch('opponent-left', undefined));
      socket.on('invite-created', (data) => this.dispatch('invite-created', data));
      socket.on('invite-error', (data) => this.dispatch('invite-error', data));
      socket.on('disconnect', (reason) => this.dispatch('disconnect', reason));
    });
    return this.connectPromise;
  }

  on(event: string, handler: Handler) {
    (this.handlers[event] ??= []).push(handler);
  }

  off(event: string, handler: Handler) {
    const list = this.handlers[event];
    if (!list) {
      return;
    }
    const index = list.indexOf(handler);
    if (index >= 0) {
      list.splice(index, 1);
    }
  }

  private dispatch(event: string, payload: unknown) {
    (this.handlers[event] ?? []).forEach((handler) => handler(payload));
  }

  /** Enter the matchmaking queue; the server emits `match-start` when ready. */
  requestMatch() {
    this.socket?.emit('matchmake');
  }

  /** Leave the matchmaking queue. */
  cancelMatch() {
    this.socket?.emit('cancel-matchmake');
  }

  /** Create an invite-only room; the server emits `invite-created` with the code. */
  createInvite() {
    this.socket?.emit('invite-create');
  }

  /** Join an invite-only room by its 6-character code. */
  joinInvite(code: string) {
    this.socket?.emit('invite-join', { code });
  }

  /** Cancel the invite room this socket is hosting. */
  cancelInvite() {
    this.socket?.emit('invite-cancel');
  }

  /** Send a game action; the server relays it to both players. */
  sendAction(action: OnlineAction) {
    this.socket?.emit('action', action);
  }

  disconnect() {
    this.connectPromise = null;
    this.socket?.disconnect();
    this.socket = null;
  }
}

export const multiplayer = new MultiplayerConnection();
